"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { ApiError, api, loadTokens } from "@/lib/api";

/**
 * EPIC-044 follow-up — Follow Topic toggle on /topic/{slug}.
 *
 * Anonymous viewers see the button but click → /login redirect.
 * Authed users get the toggle: click to follow, click again to unfollow.
 *
 * Inline follower count beside the button — readable signal of how
 * popular a topic is + sets a clear social-proof prompt for hesitant
 * users.
 */
export function FollowTopicButton({ slug }: { slug: string }) {
  const router = useRouter();
  const [signedIn, setSignedIn] = useState<boolean | null>(null);
  const [following, setFollowing] = useState(false);
  const [followerCount, setFollowerCount] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setSignedIn(loadTokens() !== null);
    let cancelled = false;
    void api
      .getTopicFollowState(slug)
      .then((res) => {
        if (cancelled) return;
        setFollowing(res.following);
        setFollowerCount(res.followerCount);
        setLoaded(true);
      })
      .catch(() => {
        if (cancelled) return;
        setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  async function handleClick() {
    if (!signedIn) {
      router.push(`/login?next=/topic/${slug}`);
      return;
    }
    if (submitting) return;
    const wasFollowing = following;
    // Optimistic toggle.
    setFollowing(!wasFollowing);
    setFollowerCount((c) => c + (wasFollowing ? -1 : 1));
    setSubmitting(true);
    try {
      if (wasFollowing) {
        await api.unfollowTopic(slug);
      } else {
        await api.followTopic(slug);
      }
    } catch (err) {
      // Revert on failure.
      setFollowing(wasFollowing);
      setFollowerCount((c) => c + (wasFollowing ? 1 : -1));
      if (err instanceof ApiError && err.status === 401) {
        router.push(`/login?next=/topic/${slug}`);
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (!loaded) {
    return (
      <div className="flex items-center gap-2">
        <span className="rounded-md border border-[var(--color-border)] px-3 py-1 text-[11px] text-[var(--color-text3)]">
          Loading…
        </span>
      </div>
    );
  }

  const label = following ? "Following" : "Follow";
  const ariaPressed = following;

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={handleClick}
        disabled={submitting}
        aria-pressed={ariaPressed}
        aria-label={following ? "Unfollow topic" : "Follow topic"}
        className="rounded-md px-3 py-1 text-[11px] font-bold transition-colors disabled:opacity-50"
        style={{
          background: following
            ? "transparent"
            : "var(--color-brand)",
          border: following
            ? "1px solid var(--color-border)"
            : "1px solid var(--color-brand)",
          color: following ? "var(--color-text2)" : "white",
        }}
      >
        {label}
      </button>
      <span className="text-[11px] text-[var(--color-text3)]">
        {followerCount} {followerCount === 1 ? "follower" : "followers"}
      </span>
    </div>
  );
}
